import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatCurrency } from 'src/utils/currency';
import { toNumber } from 'src/utils/math';
import { convertMilesToKilometers } from 'src/utils/unitSystemUtils';
import { hasValue } from 'src/utils/validation';

const TWO_WAY_TOW_SERVICE_TYPE = 'TWT';
const OUT_OF_NETWORK_PROVIDER_CATEGORY = 'Out Of Network';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    color: theme.palette.secondary.main,
  },
  lineItem: {
    padding: theme.spacing(1.25, 2),
  },
  amount: {
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'right',
  },
  total: {
    padding: theme.spacing(1.5, 2),
  },
}));

const calculateBillableMiles = (miles, freeMiles) => Number(
  Math.max(0, toNumber(miles) - toNumber(freeMiles)).toFixed(2)
);
const formatRateDisplay = (rawValue) => (hasValue(rawValue) ? formatCurrency(toNumber(rawValue)) : 'N/A');

function CalculationBreakdown({ purchaseOrderDetails, isMetric }) {
  const classes = useStyles();

  if (!purchaseOrderDetails) {
    return null;
  }

  const distanceUnit = isMetric ? 'kilometers' : 'miles';
  const rateUnit = isMetric ? 'kilometer' : 'mile';
  const serviceRate = toNumber(purchaseOrderDetails.ServiceRate);
  const serviceRateDisplay = formatRateDisplay(purchaseOrderDetails.ServiceRate);
  const loadedMileageRate = toNumber(purchaseOrderDetails.LoadedMileageRate);
  const unloadedMileageRate = toNumber(purchaseOrderDetails.UnloadedMileageRate);
  const freeLoadedMiles = toNumber(purchaseOrderDetails.FreeLoadedMiles);
  const freeUnloadedMiles = toNumber(purchaseOrderDetails.FreeUnloadedMiles);
  const hasStoredLoadedKilometers = isMetric && purchaseOrderDetails.LoadedKilometers != null;
  const hasStoredUnloadedKilometers = isMetric && purchaseOrderDetails.UnLoadedKilometers != null;
  const additionalLoadedMiles = toNumber(purchaseOrderDetails.AdditionalLoadedMiles);
  const additionalUnloadedMiles = toNumber(purchaseOrderDetails.AdditionalUnloadedMiles);
  const additionalLoadedDistance = isMetric
    ? (purchaseOrderDetails.AdditionalLoadedKilometers != null
      ? toNumber(purchaseOrderDetails.AdditionalLoadedKilometers)
      : toNumber(convertMilesToKilometers(additionalLoadedMiles)))
    : additionalLoadedMiles;
  const additionalUnloadedDistance = isMetric
    ? (purchaseOrderDetails.AdditionalUnloadedKilometers != null
      ? toNumber(purchaseOrderDetails.AdditionalUnloadedKilometers)
      : toNumber(convertMilesToKilometers(additionalUnloadedMiles)))
    : additionalUnloadedMiles;
  const unloadedMiles = hasStoredUnloadedKilometers
    ? toNumber(purchaseOrderDetails.UnLoadedKilometers)
    : toNumber(purchaseOrderDetails.MilesUnloaded) + additionalUnloadedDistance;
  const loadedLeg2Miles = additionalLoadedDistance;
  const loadedLeg1Miles = hasStoredLoadedKilometers
    ? Number(Math.max(0, toNumber(purchaseOrderDetails.LoadedKilometers) - loadedLeg2Miles).toFixed(2))
    : toNumber(purchaseOrderDetails.MilesLoaded);
  const billableUnloadedMiles = calculateBillableMiles(unloadedMiles, freeUnloadedMiles);
  const billableLoadedLeg1Miles = calculateBillableMiles(loadedLeg1Miles, freeLoadedMiles);
  const billableLoadedLeg2Miles = calculateBillableMiles(loadedLeg2Miles, freeLoadedMiles);
  const isTwoWayTow = purchaseOrderDetails.RequestedServiceType === TWO_WAY_TOW_SERVICE_TYPE;
  const isOutOfNetwork = purchaseOrderDetails.ProviderCategory === OUT_OF_NETWORK_PROVIDER_CATEGORY;

  const lineItems = isTwoWayTow
    ? [
      {
        label: isMetric ? 'Unloaded Kilometers' : 'Unloaded Miles',
        description: `Provider Location to Replacement Location: ${unloadedMiles} ${distanceUnit} - ${freeUnloadedMiles} free = ${billableUnloadedMiles} billable ${distanceUnit} @ ${formatCurrency(unloadedMileageRate)}/${rateUnit}`,
        amount: billableUnloadedMiles * unloadedMileageRate,
      },
      {
        label: 'Hook Rate 1',
        description: `Picking up the replacement car: ${serviceRateDisplay}`,
        amount: serviceRate,
      },
      {
        label: isMetric ? 'Loaded Kilometers Leg 1' : 'Loaded Miles Leg 1',
        description: `Leg 1: ${loadedLeg1Miles} ${distanceUnit} - ${freeLoadedMiles} free = ${billableLoadedLeg1Miles} billable ${distanceUnit} @ ${formatCurrency(loadedMileageRate)}/${rateUnit}`,
        amount: billableLoadedLeg1Miles * loadedMileageRate,
      },
      ...(isOutOfNetwork ? [] : [{
        label: 'Hook Rate 2',
        description: `Picking up the disabled vehicle: ${serviceRateDisplay}`,
        amount: serviceRate,
      }]),
      {
        label: isMetric ? 'Loaded Kilometers Leg 2' : 'Loaded Miles Leg 2',
        description: `Leg 2: ${loadedLeg2Miles} ${distanceUnit} - ${freeLoadedMiles} free = ${billableLoadedLeg2Miles} billable ${distanceUnit} @ ${formatCurrency(loadedMileageRate)}/${rateUnit}`,
        amount: billableLoadedLeg2Miles * loadedMileageRate,
      },
    ]
    : [
      {
        label: 'Hook Rate',
        description: `Standard hook rate: ${serviceRateDisplay}`,
        amount: serviceRate,
      },
      {
        label: isMetric ? 'Loaded Kilometers' : 'Loaded Miles',
        description: `${loadedLeg1Miles} ${distanceUnit} - ${freeLoadedMiles} free = ${billableLoadedLeg1Miles} billable ${distanceUnit} @ ${formatCurrency(loadedMileageRate)}/${rateUnit}`,
        amount: billableLoadedLeg1Miles * loadedMileageRate,
      },
    ];

  const calculatedTotal = lineItems.reduce((total, lineItem) => total + lineItem.amount, 0);

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        title="Calculation Breakdown"
        subheader="Stored mileage and rate components used for this purchase order."
      />
      <Divider />
      {lineItems.map((lineItem) => (
        <Grid alignItems="center" className={classes.lineItem} container key={lineItem.label} spacing={2}>
          <Grid item sm={9} xs={12}>
            <Typography variant="subtitle2">{lineItem.label}</Typography>
            <Typography color="textSecondary" variant="body2">
              {lineItem.description}
            </Typography>
          </Grid>
          <Grid item sm={3} xs={12}>
            <Typography className={classes.amount} variant="subtitle2">
              {lineItem.label.startsWith('Hook Rate') ? serviceRateDisplay : formatCurrency(lineItem.amount)}
            </Typography>
          </Grid>
        </Grid>
      ))}
      <Divider />
      <Grid alignItems="center" className={classes.total} container spacing={2}>
        <Grid item sm={9} xs={12}>
          <Typography variant="subtitle1">Calculated Total</Typography>
        </Grid>
        <Grid item sm={3} xs={12}>
          <Typography className={classes.amount} variant="subtitle1">
            {formatCurrency(calculatedTotal)}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );
}

CalculationBreakdown.propTypes = {
  purchaseOrderDetails: PropTypes.shape({
    RequestedServiceType: PropTypes.string,
    ProviderCategory: PropTypes.string,
    ServiceRate: PropTypes.number,
    LoadedMileageRate: PropTypes.number,
    UnloadedMileageRate: PropTypes.number,
    FreeLoadedMiles: PropTypes.number,
    FreeUnloadedMiles: PropTypes.number,
    LoadedKilometers: PropTypes.number,
    UnLoadedKilometers: PropTypes.number,
    AdditionalLoadedKilometers: PropTypes.number,
    AdditionalUnloadedKilometers: PropTypes.number,
    MilesUnloaded: PropTypes.number,
    MilesLoaded: PropTypes.number,
    AdditionalLoadedMiles: PropTypes.number,
    AdditionalUnloadedMiles: PropTypes.number,
  }),
  isMetric: PropTypes.bool,
};

CalculationBreakdown.defaultProps = {
  purchaseOrderDetails: null,
  isMetric: false,
};

export default CalculationBreakdown;